import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  fetchEmailVerificationStatus,
  handelSpeakerEmailVerify,
  showSuccess,
} from "store/slices";
import { getDataFromLocalStorage, storeLocalStorageData } from "utils/helpers";
import "./VerificationGate.scss";

// Members who have registered but not verified their email keep limited access:
// profile, verification status and resend, notifications. Everything else is
// locked until they verify (#1810).
const allowedWhileUnverified = [
  "/dashboard",
  "membership-confirmed",
  "my-profile",
  "notification",
  "inbox",
  "email-redirect",
  "new-password",
  "complete-profile",
];

const isAllowedPath = (pathname = "") =>
  allowedWhileUnverified.some((part) => pathname.includes(part));

// professional, institutional, corporate, student
const memberUserTypes = ["2", "3", "4", "5"];

const VerificationGate = ({ children }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const params = useParams();
  const {
    registration_email_otp_status: storedStatus,
    email_id: storedEmail,
    user_type: userType,
    member_id: memberId,
  } = getDataFromLocalStorage() || {};

  // Only new self-registered members are gated: their Member ID is issued when
  // they verify. Existing members, resources and admins already have one.
  const needsVerification =
    memberUserTypes.includes(userType) && !memberId && storedStatus !== "1";

  const [isVerified, setIsVerified] = useState(!needsVerification);
  const [isChecking, setIsChecking] = useState(needsVerification);
  const [isResending, setIsResending] = useState(false);

  const checkStatus = useCallback(async () => {
    const response = await dispatch(fetchEmailVerificationStatus());
    const verified = !!response?.data?.is_email_verified;
    if (verified) {
      storeLocalStorageData({ registration_email_otp_status: "1" });
    }
    setIsVerified(verified);
    setIsChecking(false);
    return verified;
  }, [dispatch]);

  useEffect(() => {
    if (!needsVerification) return;
    checkStatus();
  }, [needsVerification, checkStatus]);

  const handleResend = async () => {
    if (isResending) return;
    setIsResending(true);
    const response = await dispatch(
      handelSpeakerEmailVerify({ email: storedEmail })
    );
    if (response?.status === 200) {
      dispatch(showSuccess(response?.message));
    }
    setIsResending(false);
  };

  // while the first check is in flight, keep showing the page to avoid a flash
  if (isVerified || isChecking) {
    return children;
  }

  const banner = (
    <div className="verification-banner" role="status">
      <span className="vb-icon" aria-hidden="true">!</span>
      <span className="vb-text">
        Verify your email to get your Membership ID and full access.
        {storedEmail ? <strong> {storedEmail}</strong> : null}
      </span>
      <span className="vb-actions">
        <button type="button" onClick={handleResend}>
          {isResending ? "Sending…" : "Resend email"}
        </button>
        <button type="button" onClick={checkStatus}>
          I've verified
        </button>
      </span>
    </div>
  );

  if (isAllowedPath(pathname)) {
    return (
      <>
        {banner}
        {children}
      </>
    );
  }

  return (
    <>
      {banner}
      <div className="verification-locked">
        <div className="vl-card">
          <span className="vl-lock" aria-hidden="true">🔒</span>
          <h2>Verify your email to unlock this</h2>
          <p>
            We sent a verification link to
            {storedEmail ? <strong> {storedEmail}</strong> : " your registered email address"}.
            Open it to get your Membership ID and full access to your membership dashboard.
          </p>
          <div className="vl-actions">
            <button type="button" className="vl-primary" onClick={handleResend}>
              {isResending ? "Sending…" : "Resend verification email"}
            </button>
            <button
              type="button"
              className="vl-secondary"
              onClick={() => navigate(`/${params.memberType || "professional"}/dashboard`)}
            >
              Back to dashboard
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default VerificationGate;
